import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { ArrowLeft, ArrowSquareOut, MapPin, ShieldCheck, ChartLineUp, TrendDown, CheckCircle, Scales } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StateCoverageDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/coverage/states/${slug}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                if (e?.response?.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [slug]);

    // Title + meta + JSON-LD + canonical
    useEffect(() => {
        if (!data) return;
        const origin = window.location.origin;
        const prevTitle = document.title;
        const titleStat = data.has_stats
            ? ` — avg ${data.avg_settlement_pct}% settled on ${data.settled_count} cases`
            : "";
        document.title = `${data.name} judgment lookup & settlement${titleStat} | LienLift Direct`;

        const desc = data.has_stats
            ? `${data.name} judgments resolve for an average of ${data.avg_settlement_pct}% of balance owed across ${data.settled_count} verified settlements on LienLift Direct. Free portal: ${data.portal_name}.`
            : `${data.name} judgment lookup via ${data.portal_name}. Capture, dispute and settle ${data.name} judgments end-to-end with LienLift Direct's AI-drafted letters and Stripe-paid settlement offers.`;

        const setMeta = (name, content) => {
            let m = document.querySelector(`meta[name="${name}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
            m.setAttribute("content", content);
        };
        const setOg = (property, content) => {
            let m = document.querySelector(`meta[property="${property}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute("property", property); document.head.appendChild(m); }
            m.setAttribute("content", content);
        };
        setMeta("description", desc);
        setMeta("robots", "index,follow");

        // Open Graph + Twitter card (per-state dynamic image)
        const ogImage = `${process.env.REACT_APP_BACKEND_URL}/api/og/state/${data.slug}.png`;
        const ogTitle = data.has_stats
            ? `${data.name} judgments — avg ${data.avg_settlement_pct}% settled`
            : `${data.name} judgment lookup & settlement`;
        setOg("og:title", ogTitle);
        setOg("og:description", desc);
        setOg("og:type", "website");
        setOg("og:url", `${origin}/coverage/${data.slug}`);
        setOg("og:image", ogImage);
        setOg("og:image:width", "1200");
        setOg("og:image:height", "630");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", ogTitle);
        setMeta("twitter:description", desc);
        setMeta("twitter:image", ogImage);

        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        const prevCanonical = canonical?.getAttribute("href");
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", `${origin}/coverage/${data.slug}`);

        // JSON-LD: Service + WebPage + BreadcrumbList + (optional) Dataset
        const graph = [
            {
                "@type": "WebPage",
                "@id": `${origin}/coverage/${data.slug}`,
                "url": `${origin}/coverage/${data.slug}`,
                "name": `${data.name} judgment lookup & settlement`,
                "description": desc,
                "isPartOf": { "@id": `${origin}/#org` },
            },
            {
                "@type": "Service",
                "name": `${data.name} judgment & lien settlement service`,
                "areaServed": { "@type": "State", "name": data.name },
                "provider": { "@type": "Organization", "name": "LienLift Direct", "url": origin },
                "description": `${data.portal_name} lookup + AI dispute letters + Stripe-paid settlement offers for ${data.name} judgments and liens.`,
                "serviceType": "Judgment information and document service",
            },
            {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", position: 1, name: "Home", item: origin },
                    { "@type": "ListItem", position: 2, name: "Court coverage", item: `${origin}/coverage` },
                    { "@type": "ListItem", position: 3, name: data.name, item: `${origin}/coverage/${data.slug}` },
                ],
            },
        ];
        if (data.has_stats) {
            graph.push({
                "@type": "Dataset",
                "name": `${data.name} judgment settlement aggregates`,
                "description": `Aggregate settlement performance across ${data.settled_count} verified ${data.name} judgments handled via LienLift Direct.`,
                "creator": { "@type": "Organization", "name": "LienLift Direct" },
                "variableMeasured": [
                    { "@type": "PropertyValue", "name": "Average settlement %", "value": data.avg_settlement_pct },
                    { "@type": "PropertyValue", "name": "Median settlement %", "value": data.median_settlement_pct },
                    { "@type": "PropertyValue", "name": "Best settlement %", "value": data.best_settlement_pct },
                    { "@type": "PropertyValue", "name": "Settled cases", "value": data.settled_count },
                ],
            });
        }

        const ld = { "@context": "https://schema.org", "@graph": graph };
        const scriptId = "ll-state-coverage-ld";
        let script = document.getElementById(scriptId);
        if (!script) {
            script = document.createElement("script");
            script.id = scriptId;
            script.type = "application/ld+json";
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(ld);

        return () => {
            document.title = prevTitle;
            const s = document.getElementById(scriptId);
            if (s) s.remove();
            if (prevCanonical && canonical) canonical.setAttribute("href", prevCanonical);
        };
    }, [data]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-sans">Loading…</div>;

    if (notFound || !data) {
        return (
            <div className="min-h-screen bg-white font-sans flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center px-6">
                    <Card className="max-w-md p-8 text-center" data-testid="state-not-found">
                        <Scales size={32} weight="duotone" className="mx-auto text-slate-400" />
                        <h1 className="font-serif text-2xl mt-4">State not yet covered</h1>
                        <p className="text-sm text-slate-600 mt-3">
                            We haven&apos;t integrated this state&apos;s court portal yet. Federal RECAP is live nationwide
                            and our manual capture form supports any U.S. judgment.
                        </p>
                        <Link to="/coverage">
                            <Button variant="outline" className="mt-5" data-testid="state-back-coverage">
                                <ArrowLeft size={14} className="mr-1.5" /> All states
                            </Button>
                        </Link>
                    </Card>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900" data-testid="state-coverage-page">
            <Header />

            <main className="max-w-[1100px] mx-auto px-6 py-16" data-testid={`state-coverage-${data.code}`}>
                <Link to="/coverage" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="state-back-link">
                    <ArrowLeft size={14} /> All states
                </Link>

                <section className="mt-8">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#0F1E3D] text-white text-xs font-mono font-semibold">
                            {data.code}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-emerald-700">
                            <CheckCircle size={13} weight="fill" /> {data.badge}
                        </span>
                    </div>
                    <h1 className="font-serif text-4xl sm:text-6xl mt-5 leading-tight tracking-tight">
                        {data.name} judgment lookup<span className="text-[#2563EB]">.</span>
                    </h1>
                    <p className="mt-5 text-lg text-slate-700 max-w-2xl">
                        LienLift Direct connects to <strong>{data.portal_name}</strong> so you can search {data.name}{" "}
                        judgments and abstracts, capture the data into a case file, then send AI-drafted dispute or
                        settlement letters — with Stripe-paid offers your creditor can accept in two clicks.
                    </p>
                    <div className="mt-6 flex gap-3 flex-wrap">
                        <a href={data.portal_url} target="_blank" rel="noreferrer">
                            <Button variant="outline" className="h-11 px-5" data-testid="state-open-portal">
                                <ArrowSquareOut size={14} className="mr-1.5" />
                                Open {data.portal_name}
                            </Button>
                        </a>
                        <Link to="/register">
                            <Button className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-6" data-testid="state-cta-register">
                                Start a {data.name} case →
                            </Button>
                        </Link>
                    </div>
                    <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
                        <ShieldCheck size={16} weight="duotone" className="text-emerald-600" />
                        <span>Information &amp; document service — not a law firm.</span>
                    </div>
                </section>

                {data.has_stats ? (
                    <>
                        <section className="mt-14" data-testid="state-stats-section">
                            <p className="overline">{data.name} settlement intelligence</p>
                            <h2 className="font-serif text-3xl mt-2">
                                {data.name} judgments resolve at an average of{" "}
                                <span className="text-[#0F1E3D]">{data.avg_settlement_pct}%</span> of balance owed.
                            </h2>
                            <p className="mt-3 text-slate-600 max-w-2xl">
                                Based on <strong>{data.settled_count}</strong> verified settlements run through LienLift Direct.
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden mt-8">
                                <Stat label="Avg accepted" value={`${data.avg_settlement_pct}%`} tint="navy" testid="stat-avg" />
                                <Stat label="Median" value={`${data.median_settlement_pct}%`} tint="blue" testid="stat-median" />
                                <Stat label="Best deal" value={`${data.best_settlement_pct}%`} tint="green" testid="stat-best" />
                                <Stat label="Worst deal" value={`${data.worst_settlement_pct}%`} tint="brown" testid="stat-worst" />
                            </div>

                            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
                                <Stat label="Settled cases" value={data.settled_count} testid="stat-count" />
                                <Stat label="Total owed" value={`$${data.total_owed.toLocaleString()}`} testid="stat-owed" />
                                <Stat label="Total saved" value={`$${data.total_savings.toLocaleString()}`} tint="green" testid="stat-saved" />
                            </div>
                        </section>

                        {data.top_creditors && data.top_creditors.length > 0 && (
                            <section className="mt-14" data-testid="state-top-creditors">
                                <div className="flex items-center gap-2">
                                    <ChartLineUp size={20} weight="duotone" className="text-[#0F1E3D]" />
                                    <h2 className="font-serif text-2xl">Top creditors settled in {data.name}</h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                                    {data.top_creditors.map((c) => (
                                        <Link key={c.slug} to={`/benchmarks/${c.slug}`} data-testid={`top-creditor-${c.slug}`}>
                                            <Card className="p-4 hover:border-[#0F1E3D] transition-colors">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <h3 className="font-medium text-slate-900">{c.creditor}</h3>
                                                        <p className="text-xs text-slate-500 mt-1">
                                                            {c.settled_count} settled · ${c.total_savings.toLocaleString()} saved
                                                        </p>
                                                    </div>
                                                    <span className="inline-flex items-center gap-1 text-emerald-700 font-mono text-sm">
                                                        <TrendDown size={13} /> {c.avg_settlement_pct}%
                                                    </span>
                                                </div>
                                            </Card>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        )}

                        {Object.keys(data.by_case_type || {}).length > 0 && (
                            <section className="mt-14" data-testid="state-case-types">
                                <p className="overline">Case-type mix</p>
                                <div className="flex flex-wrap gap-2.5 mt-3">
                                    {Object.entries(data.by_case_type).map(([t, n]) => (
                                        <span key={t} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 text-sm">
                                            <span className="font-medium capitalize">{t.replace(/_/g, " ")}</span>
                                            <span className="font-mono text-xs text-slate-500">×{n}</span>
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                ) : (
                    <section className="mt-14" data-testid="state-no-stats">
                        <Card className="p-6 bg-slate-50 border-slate-200">
                            <p className="overline">Coverage live — settlement data still aggregating</p>
                            <p className="mt-3 text-slate-700 max-w-2xl">
                                We&apos;ve integrated the {data.portal_name} so you can pull {data.name} judgments
                                today. Public settlement aggregates appear here once at least {data.k_floor} {data.name}{" "}
                                cases close on the platform (k-anonymity floor).
                            </p>
                        </Card>
                    </section>
                )}

                <section className="mt-14">
                    <div className="flex items-center gap-2 mb-4">
                        <MapPin size={20} weight="duotone" className="text-[#0F1E3D]" />
                        <h2 className="font-serif text-2xl">How {data.name} judgments work on LienLift Direct</h2>
                    </div>
                    <ol className="space-y-3 text-slate-700 max-w-3xl list-decimal pl-5">
                        <li><strong>Look up.</strong> One-click open to {data.portal_name} — or search via our in-app panel where supported.</li>
                        <li><strong>Capture.</strong> Pull the docket data into a new case file (case number, parties, amount, date, county).</li>
                        <li><strong>Draft.</strong> AI workbench produces dispute, validation, and settlement letters tuned to {data.name} judgment law.</li>
                        <li><strong>Offer.</strong> Send a public offer link with built-in Stripe checkout for pay-for-deletion settlements.</li>
                        <li><strong>Close.</strong> Signed PDF agreement + Documents digest emailed automatically.</li>
                    </ol>
                </section>

                <Card className="p-8 mt-14 bg-[#0F1E3D] text-white border-0" data-testid="state-bottom-cta">
                    <p className="overline !text-white/60">Ready to settle in {data.name}?</p>
                    <h2 className="font-serif text-3xl mt-2 leading-tight">
                        Open your first {data.name} case for free.
                    </h2>
                    <p className="mt-4 text-white/80 max-w-2xl">
                        No credit card to start. You only pay when you settle — and your debtor can pay you directly via Stripe on the offer page.
                    </p>
                    <div className="mt-6 flex gap-3 flex-wrap">
                        <Link to="/register">
                            <Button className="bg-white text-[#0F1E3D] hover:bg-white/90 h-11 px-6" data-testid="state-final-register">
                                Open a free case →
                            </Button>
                        </Link>
                        <Link to="/coverage">
                            <Button variant="ghost" className="text-white hover:bg-white/10 h-11 px-6">
                                See all states
                            </Button>
                        </Link>
                    </div>
                </Card>

                <section className="mt-12 text-xs text-slate-500 max-w-2xl">
                    <p>
                        Methodology: stats above are unweighted averages of verified LienLift Direct settlements with
                        a documented {data.name} jurisdiction. Past settlements do not predict your outcome. LienLift
                        Direct is a non-legal information &amp; document service, not a law firm.
                    </p>
                </section>
            </main>

            <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-500">
                © {new Date().getFullYear()} LienLift Direct · Information &amp; document service · Not a law firm.
            </footer>
        </div>
    );
}

function Header() {
    return (
        <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
            <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                    <BrandLogo height={36} />
                </Link>
                <div className="flex items-center gap-3">
                    <Link to="/login"><Button variant="ghost" size="sm">Sign in</Button></Link>
                    <Link to="/register"><Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">Get started</Button></Link>
                </div>
            </div>
        </header>
    );
}

function Stat({ label, value, tint, testid }) {
    const tones = {
        navy: { bg: "bg-[#0F1E3D]", text: "text-white", lab: "text-white/60" },
        blue: { bg: "bg-blue-50", text: "text-blue-900", lab: "text-blue-700" },
        green: { bg: "bg-emerald-50", text: "text-emerald-900", lab: "text-emerald-700" },
        brown: { bg: "bg-amber-50", text: "text-amber-900", lab: "text-amber-800" },
    };
    const t = tones[tint] || { bg: "bg-white", text: "text-slate-900", lab: "" };
    return (
        <div className={`p-6 ${t.bg}`} data-testid={testid}>
            <p className={`overline ${t.lab}`}>{label}</p>
            <p className={`font-mono text-3xl mt-2 font-semibold ${t.text}`}>{value}</p>
        </div>
    );
}
